/**
 * Authentication Cache Utility
 * Manages Google OAuth tokens and user profile in localStorage
 * Following OAuth 2.0 security best practices
 */

export interface StoredToken {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  refresh_token?: string; // Optional: only present with offline access
}

export interface StoredTokenData {
  token: StoredToken;
  expiresAt: number;
  issuedAt: number;
}

export interface UserProfile {
  name: string;
  picture: string;
  email: string;
}

// Storage keys - centralized for consistency
const STORAGE_KEYS = {
  TOKEN: "googleAuthToken",
  PROFILE: "googleUserProfile",
  LAST_REQUEST: "lastClaudeRequest",
} as const;

/**
 * Save authentication token to cache
 */
export function saveAuthToken(token: StoredToken): void {
  try {
    const now = new Date().getTime();
    const tokenData: StoredTokenData = {
      token,
      expiresAt: now + (token.expires_in || 3600) * 1000,
      issuedAt: now,
    };
    localStorage.setItem(STORAGE_KEYS.TOKEN, JSON.stringify(tokenData));
  } catch (error) {
    console.error("Failed to save auth token:", error);
    // If localStorage is full or unavailable, fail silently
    // User will need to re-authenticate on page refresh
  }
}

/**
 * Save user profile to cache
 */
export function saveUserProfile(profile: UserProfile): void {
  try {
    localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(profile));
  } catch (error) {
    console.error("Failed to save user profile:", error);
  }
}

/**
 * Get cached authentication token
 * Returns null if token is missing, expired, or invalid
 */
export function getCachedAuthToken(): StoredToken | null {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!storedData) return null;

    const tokenData: StoredTokenData = JSON.parse(storedData);

    // Validate token structure
    if (!tokenData.token || !tokenData.expiresAt || !tokenData.issuedAt) {
      console.warn("Invalid token structure in cache");
      clearAuthCache();
      return null;
    }

    // Check if token is expired (with 5 minute buffer for safety)
    const now = new Date().getTime();
    const bufferTime = 5 * 60 * 1000; // 5 minutes

    if (now >= tokenData.expiresAt - bufferTime) {
      // If token has a refresh_token, keep it even if expired
      // The app will automatically refresh it when loaded
      if (tokenData.token.refresh_token) {
        console.log("Access token expired but refresh token available - will auto-refresh");
        return tokenData.token;
      }

      // No refresh token - clear cache and force re-login
      console.log("Token expired and no refresh token available");
      clearAuthCache();
      return null;
    }

    return tokenData.token;
  } catch (error) {
    console.error("Failed to retrieve cached token:", error);
    clearAuthCache(); // Clear corrupted data
    return null;
  }
}

/**
 * Get cached user profile
 */
export function getCachedUserProfile(): UserProfile | null {
  try {
    const storedProfile = localStorage.getItem(STORAGE_KEYS.PROFILE);
    if (!storedProfile) return null;

    const profile: UserProfile = JSON.parse(storedProfile);

    // Validate profile structure
    if (!profile.email || !profile.name) {
      console.warn("Invalid profile structure in cache");
      localStorage.removeItem(STORAGE_KEYS.PROFILE);
      return null;
    }

    return profile;
  } catch (error) {
    console.error("Failed to retrieve cached profile:", error);
    localStorage.removeItem(STORAGE_KEYS.PROFILE);
    return null;
  }
}

/**
 * Check if token is expired or expiring soon
 * @param bufferMinutes - Minutes before expiration to consider token as expired (default: 5)
 */
export function isTokenExpired(bufferMinutes: number = 5): boolean {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!storedData) return true;

    const tokenData: StoredTokenData = JSON.parse(storedData);
    const now = new Date().getTime();
    const bufferTime = bufferMinutes * 60 * 1000;

    return now >= tokenData.expiresAt - bufferTime;
  } catch {
    return true;
  }
}

/**
 * Get time remaining until token expires (in milliseconds)
 * Returns 0 if token is expired or missing
 */
export function getTokenTimeRemaining(): number {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!storedData) return 0;

    const tokenData: StoredTokenData = JSON.parse(storedData);
    const now = new Date().getTime();
    const remaining = tokenData.expiresAt - now;

    return Math.max(0, remaining);
  } catch {
    return 0;
  }
}

/**
 * Clear all authentication data from cache
 * IMPORTANT: Always call this when:
 * - User logs out
 * - Token expires
 * - Authentication fails
 * - Redirecting back to login
 */
export function clearAuthCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.PROFILE);
  } catch (error) {
    console.error("Failed to clear auth cache:", error);
  }
}

/**
 * Check if user has valid cached authentication
 */
export function hasValidAuth(): boolean {
  return getCachedAuthToken() !== null && getCachedUserProfile() !== null;
}

/**
 * PKCE (Proof Key for Code Exchange) Helper Functions
 * Used for secure OAuth Authorization Code Flow
 */

// Storage key for PKCE code verifier (temporary, only during login flow)
const PKCE_VERIFIER_KEY = "pkce_code_verifier";

/**
 * Generate a cryptographically random code verifier for PKCE
 * Returns a base64url-encoded random string (43-128 characters)
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Generate a code challenge from the code verifier
 * Uses SHA-256 hash as per OAuth 2.0 PKCE spec
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64URLEncode(new Uint8Array(hash));
}

/**
 * Base64URL encoding (without padding)
 * Required for PKCE code verifier and challenge
 */
function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Save PKCE code verifier temporarily during OAuth flow
 * This will be used to exchange the authorization code for tokens
 */
export function savePKCEVerifier(verifier: string): void {
  try {
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  } catch (error) {
    console.error("Failed to save PKCE verifier:", error);
  }
}

/**
 * Retrieve and remove PKCE code verifier
 * Should only be used once during token exchange
 */
export function getPKCEVerifier(): string | null {
  try {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (verifier) {
      sessionStorage.removeItem(PKCE_VERIFIER_KEY); // Single use
    }
    return verifier;
  } catch (error) {
    console.error("Failed to retrieve PKCE verifier:", error);
    return null;
  }
}

/**
 * Schema Cache Warmup Helper Functions
 * Claude's schema caching expires after 24 hours, so we warmup the cache
 * periodically to ensure fast response times
 */

/**
 * Check if a warmup request is needed
 * Returns true if no request (warmup or normal) has been made in the last 12 hours
 */
export function needsWarmup(): boolean {
  try {
    const lastRequest = localStorage.getItem(STORAGE_KEYS.LAST_REQUEST);
    if (!lastRequest) return true;

    const lastRequestTime = parseInt(lastRequest, 10);
    const now = Date.now();
    const twelveHours = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

    return now - lastRequestTime >= twelveHours;
  } catch (error) {
    console.error("Failed to check warmup status:", error);
    return true; // Default to needing warmup on error
  }
}

/**
 * Record that a request (warmup or normal) was made
 * This updates the timestamp used to determine if warmup is needed
 */
export function recordRequest(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_REQUEST, Date.now().toString());
  } catch (error) {
    console.error("Failed to record request timestamp:", error);
  }
}
